import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

// Popup will re-show after this many hours since dismissal
const HIDE_FOR_HOURS = 24;
const STORAGE_KEY = 'popupDismissedUntil';

export default function PopupBanner() {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't fetch if user dismissed recently
    const dismissedUntil = localStorage.getItem(STORAGE_KEY);
    if (dismissedUntil && Date.now() < Number(dismissedUntil)) return;

    fetch('/api/popup')
      .then(r => r.json())
      .then(data => {
        if (!data.active) return;
        setPopup(data);
        setVisible(true);
      })
      .catch(() => {}); // popup is non-critical — fail silently
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now() + HIDE_FOR_HOURS * 60 * 60 * 1000);
    setVisible(false);
  };

  if (!visible || !popup) return null;

  // Determine if ctaUrl is internal (react-router Link) or external (plain <a>)
  const isExternal = popup.ctaUrl && /^https?:\/\//.test(popup.ctaUrl);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={e => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {popup.badge && (
          <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
            {popup.badge}
          </span>
        )}

        <h2 className="text-2xl font-bold text-gray-900 mb-3">{popup.title}</h2>
        <p className="text-gray-600 mb-6 leading-relaxed">{popup.body}</p>

        {popup.ctaUrl && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {isExternal ? (
              <a
                href={popup.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={dismiss}
                className="inline-block bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors"
              >
                {popup.ctaText || 'Learn More'}
              </a>
            ) : (
              <Link
                to={popup.ctaUrl}
                onClick={dismiss}
                className="inline-block bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors"
              >
                {popup.ctaText || 'Learn More'}
              </Link>
            )}
            <button
              onClick={dismiss}
              className="text-gray-500 hover:text-gray-700 px-4 py-3 text-sm transition-colors"
            >
              Maybe later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
